/**
 * Event Format Module - 事件格式轉換、驗證、MQTT 轉換
 */

import {
  EVENT_TYPES,
  EVENT_PRIORITIES,
  EVENT_SOURCES,
  EVENT_LEVELS
} from './constants'
import type {
  EventRow,
  EventType,
  EventPriority,
  EventSource,
  EventLevel,
  DisplayEvent,
  MqttData,
  EventServiceContext
} from './types'

// ============ 格式建立 ============

/** 建立標準事件物件（所有欄位帶預設值） */
export function createStandardEvent(
  ctx: EventServiceContext,
  options: Partial<EventRow> = {}
): EventRow {
  const {
    id = `event_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    type = EVENT_TYPES.ALARM,
    priority = EVENT_PRIORITIES.MEDIUM,
    source = EVENT_SOURCES.DEVICE,
    title = '',
    content = '',
    organization_id = ctx.fieldStore?.selectedOrgId || '',
    organization_name = '',
    field_id = ctx.fieldStore?.selectedFieldId || '',
    field_name = '',
    system_id = '',
    system_name = '',
    device_id = '',
    device_name = '',
    event_level = EVENT_LEVELS.DEVICE,
    source_detail = '',
    location = null,
    temperature = null,
    extra_data = {},
    attachments = [],
    actions = [],
    is_read = false,
    is_resolved = false
  } = options

  return {
    id,
    type,
    priority,
    source,
    title,
    content,
    organization_id,
    organization_name,
    field_id,
    field_name,
    system_id,
    system_name,
    device_id,
    device_name,
    event_level,
    source_detail,
    location,
    temperature,
    extra_data,
    attachments,
    actions,
    is_read,
    is_resolved,
    resolved_by: null,
    resolved_at: null,
    resolved_note: null,
    duration_minutes: null,
    risk_type: null,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
    deleted_at: null
  }
}

// ============ 格式轉換 ============

/** 將事件轉換為顯示格式或標準格式 */
export function convertEventFormat(
  ctx: EventServiceContext,
  event: EventRow,
  targetFormat: 'display' | 'standard'
): DisplayEvent | EventRow {
  switch (targetFormat) {
    case 'display':
      return {
        id: event.id,
        title: event.title,
        message: event.content,
        content: event.content,
        type: event.type,
        priority: event.priority,
        source: event.event_level,
        fieldName: event.field_name || '未知案場',
        deviceName: event.device_name || '',
        temperature: event.temperature,
        extraInfo: event.source_detail || '',
        timestamp: new Date(event.created_at),
        eventId: event.id,
        fieldId: event.field_id,
        deviceId: event.device_id,
        read: Boolean(event.is_read),
        resolved: Boolean(event.is_resolved),
        attachments: Array.isArray(event.attachments) ? event.attachments : [],
        actions: Array.isArray(event.actions) ? event.actions : []
      }

    case 'standard':
      return createStandardEvent(ctx, {
        type: event.type,
        priority: event.priority,
        source: event.source || EVENT_SOURCES.DEVICE,
        title: event.title,
        content: event.content || '',
        field_name: event.field_name,
        device_name: event.device_name,
        temperature: event.temperature,
        source_detail: event.source_detail || `${event.field_name || ''} ${event.device_name || ''}`.trim()
      })

    default:
      throw new Error(`不支援的目標格式: ${targetFormat}`)
  }
}

/** 批量轉換事件格式 */
export function convertEventsToDisplayFormat(
  ctx: EventServiceContext,
  events: EventRow[]
): DisplayEvent[] {
  return events.map(event => convertEventFormat(ctx, event, 'display') as DisplayEvent)
}

export function convertEventsToStandardFormat(
  ctx: EventServiceContext,
  events: EventRow[]
): EventRow[] {
  return events.map(event => convertEventFormat(ctx, event, 'standard') as EventRow)
}

// ============ MQTT 轉換 ============

/** 將 MQTT 資料轉換為標準事件格式 */
export function transformMqttDataToEvent(
  ctx: EventServiceContext,
  data: MqttData,
  priority: EventPriority
): EventRow {
  if (data.eventType && data.payload) {
    console.log("[EventService] 正在處理新的 '信封模式' MQTT 事件")
    const { eventId, source, timestamp, payload } = data
    const { device, alarm, telemetry, event_level, source_detail } = payload || {}

    if (!alarm) {
      console.warn("[EventService] 信封模式事件缺少 alarm 物件，使用預設值", data)
    }
    if (!device) {
      console.warn("[EventService] 信封模式事件缺少 device 物件，使用預設值", data)
    }

    const severityToPriorityMap: Record<string, EventPriority> = {
      'critical': EVENT_PRIORITIES.HIGH,
      'high': EVENT_PRIORITIES.HIGH,
      'error': EVENT_PRIORITIES.HIGH,
      'warning': EVENT_PRIORITIES.MEDIUM,
      'medium': EVENT_PRIORITIES.MEDIUM,
      'normal': EVENT_PRIORITIES.MEDIUM,
      'info': EVENT_PRIORITIES.LOW,
      'low': EVENT_PRIORITIES.LOW,
      'debug': EVENT_PRIORITIES.LOW,
      'none': EVENT_PRIORITIES.LOW,
    }

    const alarmSeverity = alarm?.severity
    const finalPriority = alarmSeverity ? (severityToPriorityMap[alarmSeverity] || priority) : priority

    const standardEvent = createStandardEvent(ctx, {
      id: eventId,
      type: (alarm?.type as EventType) || EVENT_TYPES.ALARM,
      priority: finalPriority,
      source: (source as EventSource) || EVENT_SOURCES.MQTT,
      title: alarm?.title || '設備事件',
      content: alarm?.description || '',
      field_id: device?.field_id || '',
      field_name: device?.field_name || '',
      device_id: device?.id || '',
      device_name: device?.name || '',
      event_level: event_level || EVENT_LEVELS.DEVICE,
      source_detail: source_detail || '',
      ...(telemetry || {}),
      extra_data: payload?.original_data || {}
    })

    if (timestamp) {
      standardEvent.created_at = new Date(timestamp).toISOString()
      standardEvent.updated_at = new Date(timestamp).toISOString()
    }

    return standardEvent
  }

  console.warn("[EventService] 收到舊版 (非信封模式) MQTT 事件，請考慮升級事件來源。", data)
  const eventId = `mqtt_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`

  const values = data.values || {}
  const title = (values.title as string) || data.title || '設備事件'
  const content = (values.message as string) || (values.content as string) || data.message || data.content || ''

  return {
    id: eventId,
    type: data.type || EVENT_TYPES.ALARM,
    priority: priority,
    source: EVENT_SOURCES.MQTT,
    title: title,
    content: content,
    organization_id: (values.organization_id as string) || data.organization_id || ctx.fieldStore?.selectedOrgId || '',
    organization_name: (values.organization_name as string) || data.organization_name || '',
    field_id: (values.field_id as string) || data.field_id || '',
    field_name: (values.field_name as string) || data.field_name || '',
    system_id: (values.system_id as string) || data.system_id || '',
    system_name: (values.system_name as string) || data.system_name || '',
    device_id: (values.device_id as string) || (values.device_code as string) || data.device_id || '',
    device_name: (values.device_name as string) || data.device_name || '',
    event_level: data.event_level || EVENT_LEVELS.DEVICE,
    source_detail: (values.source_detail as string) || data.source_detail || '',
    location: (values.location as unknown) || data.location || null,
    temperature: (values.temperature as number) || data.temperature || null,
    extra_data: (values.extra_data as Record<string, unknown>) || data.extra_data || {},
    attachments: (data.attachments || []) as EventRow['attachments'],
    actions: (data.actions || []) as EventRow['actions'],
    is_read: false,
    is_resolved: false,
    resolved_by: null,
    resolved_at: null,
    resolved_note: null,
    duration_minutes: null,
    risk_type: null,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
    deleted_at: null
  }
}

// ============ 純函式工具 ============

/** 產生事件預設動作 */
export function generateDefaultActions(event: EventRow): Array<{ id: string; label: string; type: string }> {
  const actions: Array<{ id: string; label: string; type: string }> = []

  if (event.type === EVENT_TYPES.ALARM) {
    actions.push(
      { id: 'create_dispatch', label: '創建工單', type: 'primary' },
      { id: 'ignore', label: '忽略', type: 'default' }
    )
  } else if (event.type === EVENT_TYPES.MAINTENANCE) {
    actions.push(
      { id: 'create_maintenance', label: '創建維護計劃', type: 'primary' },
      { id: 'postpone', label: '延後', type: 'default' }
    )
  } else if (event.type === EVENT_TYPES.TEMPERATURE) {
    actions.push(
      { id: 'check_device', label: '檢查設備', type: 'primary' },
      { id: 'create_alert', label: '創建警告', type: 'warning' }
    )
  }

  return actions
}

/** 事件類型對應案場事件類型 */
export function mapEventTypeToFieldEventType(eventType: EventType): string {
  const typeMap: Record<string, string> = {
    [EVENT_TYPES.ALARM]: 'repair',
    [EVENT_TYPES.MAINTENANCE]: 'maintenance',
    [EVENT_TYPES.SYSTEM]: 'update',
    [EVENT_TYPES.INFO]: 'inspection',
    [EVENT_TYPES.WARNING]: 'repair',
    [EVENT_TYPES.TEMPERATURE]: 'inspection',
    [EVENT_TYPES.CONDITION]: 'inspection',
    [EVENT_TYPES.TIMEOUT]: 'maintenance',
    [EVENT_TYPES.RECOVERY]: 'update',
    [EVENT_TYPES.OTHER]: 'other',
    [EVENT_TYPES.INCIDENT]: 'repair',
    [EVENT_TYPES.RISK]: 'inspection'
  }
  return typeMap[eventType] || 'other'
}

/** 驗證事件格式是否正確 */
export function validateEventFormat(event: EventRow): boolean {
  const requiredFields = ['id', 'type', 'priority', 'source', 'title'] as const
  const missingFields = requiredFields.filter(field => !event[field])

  if (missingFields.length > 0) {
    throw new Error(`事件格式驗證失敗，缺少必要欄位: ${missingFields.join(', ')}`)
  }

  const validTypes = Object.values(EVENT_TYPES)
  const validPriorities = Object.values(EVENT_PRIORITIES)
  const validSources = Object.values(EVENT_SOURCES)
  const validLevels = Object.values(EVENT_LEVELS)

  if (!validTypes.includes(event.type)) {
    throw new Error(`無效的事件類型: ${event.type}`)
  }
  if (!validPriorities.includes(event.priority)) {
    throw new Error(`無效的優先級: ${event.priority}`)
  }
  if (!validSources.includes(event.source)) {
    throw new Error(`無效的來源: ${event.source}`)
  }
  if (event.event_level && !validLevels.includes(event.event_level)) {
    throw new Error(`無效的事件層級: ${event.event_level}`)
  }

  return true
}
